/**
 * The shop's two pre-launch switches (T13-41), read from the shop's details record that every
 * screen already keeps live (`useStoreSettings`, cms `cms.store_settings.updated` → ≤ 2 s, R21):
 *
 * - `secondhandLicensed` — 公安委員会 + 古物商許可番号 saved in Settings → Shop details. Until then
 *   the services refuse 買取 and 委託受付 (`secondhand_license_required`) — the admin shows a bar.
 * - `webOrdersPaused` — "Pause web orders" (owner / manager, `orders.edit`): checkout on the website
 *   answers 503, the shop counter (POS) keeps selling.
 */
import { useQueryClient } from "@tanstack/react-query";
import { createCmsClient } from "@tcg/api-client";
import { useCallback } from "react";

import { unwrap } from "@/lib/api/errors";
import { useServiceClient } from "@/lib/api/use-api";

import { STORE_SETTINGS_KEY, type StoreSettingsOut, useStoreSettings } from "./storeSettingsApi";

export interface ShopSwitches {
  /** `null` while the record is loading / could not be read (never show a wrong bar). */
  secondhandLicensed: boolean | null;
  webOrdersPaused: boolean | null;
}

export function shopSwitchesOf(record: StoreSettingsOut | undefined): ShopSwitches {
  if (!record) {
    return { secondhandLicensed: null, webOrdersPaused: null };
  }
  return {
    secondhandLicensed: record.secondhandLicensed === true,
    webOrdersPaused: record.webOrdersPaused === true,
  };
}

export function useShopSwitches(): ShopSwitches {
  const store = useStoreSettings();
  return shopSwitchesOf(store.data);
}

/** `PUT /v1/admin/settings/store/web-orders {paused}` — audited by cms; the answer is the record. */
export function useSetWebOrdersPaused(): (paused: boolean) => Promise<StoreSettingsOut> {
  const cms = useServiceClient(createCmsClient);
  const queryClient = useQueryClient();
  return useCallback(
    async (paused: boolean) => {
      const record = await unwrap(
        cms.PUT("/v1/admin/settings/store/web-orders", { body: { paused } }),
      );
      queryClient.setQueryData(STORE_SETTINGS_KEY, record);
      return record;
    },
    [cms, queryClient],
  );
}
